import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { ROUTES } from "@/config/constants";
import {
  createCompetenciaApi,
  extractGgError,
  fetchCompetencias,
  moverCompetenciaApi,
  removeCompetenciaApi,
  updateCompetenciaApi,
  type GgCompetenciaDto,
} from "@/services/genteGestao";

export function GenteGestaoLiderancaCompetenciasPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<GgCompetenciaDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [novoNome, setNovoNome] = useState("");
  const [novaDesc, setNovaDesc] = useState("");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchCompetencias());
    } catch (e) {
      setError(extractGgError(e, "Não foi possível carregar as competências."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function criar() {
    if (!novoNome.trim()) return;
    try {
      await createCompetenciaApi({ nome: novoNome.trim(), descricao: novaDesc.trim() });
      setNovoNome("");
      setNovaDesc("");
      await load();
    } catch (e) {
      setError(extractGgError(e, "Não foi possível criar."));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <button
        className="self-start text-sm text-brand-700 hover:underline"
        onClick={() => navigate(ROUTES.GENTE_GESTAO_LIDERANCA)}
      >
        ← Ciclos de liderança
      </button>
      <p className="text-sm text-slate-500">
        Catálogo reutilizável. Os ciclos copiam o nome da competência no momento da pergunta, então o histórico
        permanece estável.
      </p>
      {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-card sm:grid-cols-2">
        <Input label="Nova competência" value={novoNome} onChange={(e) => setNovoNome(e.target.value)} />
        <Input label="Descrição" value={novaDesc} onChange={(e) => setNovaDesc(e.target.value)} />
        <div className="sm:col-span-2">
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => void criar()}>
            Adicionar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader label="Carregando competências..." />
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((c, idx) => (
            <li key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="font-semibold text-slate-700">#{c.ordem}</span>
                <span>{c.codigo}</span>
                <label className="ml-auto flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={c.ativo}
                    onChange={(e) => void updateCompetenciaApi(c.id, { ativo: e.target.checked }).then(load)}
                  />
                  Ativa
                </label>
              </div>
              <Input
                value={c.nome}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== c.nome) void updateCompetenciaApi(c.id, { nome: v }).then(load);
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, nome: v } : x)));
                }}
              />
              <textarea
                className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm"
                rows={2}
                value={c.descricao ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, descricao: v } : x)));
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (c.descricao ?? "")) void updateCompetenciaApi(c.id, { descricao: v }).then(load);
                }}
              />
              <div className="mt-2 flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={idx === 0}
                  onClick={() => void moverCompetenciaApi(c.id, "up").then(setItems)}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={idx === items.length - 1}
                  onClick={() => void moverCompetenciaApi(c.id, "down").then(setItems)}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void removeCompetenciaApi(c.id)
                      .then(load)
                      .catch((e) => setError(extractGgError(e, "Não foi possível excluir.")))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
